import {
  createRef,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useNavigate} from 'react-router';

import type {GoalApiModel as Goal} from '@yourcoach/shared/api/goal';
import type {CollectionStore} from '@yourcoach/shared/api/utils/collectionStore';
import type {ProgramType} from '@yourcoach/shared/utils/entity/program';

import {i18n} from '@yourcoach/i18n';
import {currentUserStore} from '@yourcoach/shared/api/user';
import {alert} from '@yourcoach/shared/components/alert';
import {ProgramTypeEnum} from '@yourcoach/shared/utils/entity/program';
import {emitter} from '@yourcoach/shared/utils/eventEmitter';
import {logger} from '@yourcoach/shared/utils/logger';

import {routerPath} from '@src/routes/path';

import type {ProgramT} from '.';

import Complete from './Complete';
import Description from './Description';
import Details, {PROGRAM_TYPE_CHANGE_EVENT} from './Details';
import Goals from './Goals';
import Library from './Library';
import Schedule from './schedule';

export type Tab = {
  content: ReactElement<TabProps>;
  key:
    | 'complete'
    | 'description'
    | 'details'
    | 'goals'
    | 'library'
    | 'schedule';
  title: string;
};

export type TabProps = {
  cloneProgram: () => void;
  goalsStore?: CollectionStore<Goal>;
  isActive: boolean;
  isIndividual: boolean;
  isOfferedProgram: boolean;
  program: null | ProgramT;
  shouldCloneProgram: boolean;
  userIsOwner: boolean;
};

export type TabRef = {
  getData: (direction?: 'back' | 'next') => any;
};

const useTabs = (
  program: null | ProgramT,
  activeTabIndex: number,
  goalsStore: CollectionStore<Goal>,
) => {
  const navigate = useNavigate();

  const refs = useRef({
    complete: createRef<TabRef>(),
    description: createRef<TabRef>(),
    details: createRef<TabRef>(),
    goals: createRef<TabRef>(),
    library: createRef<TabRef>(),
    schedule: createRef<TabRef>(),
  }).current;

  const userIsOwner = useMemo(
    () =>
      program && program._id
        ? currentUserStore.user!._id === program.user_id
        : true,
    [program],
  );

  const shouldCloneProgram = useMemo(
    () =>
      !!(
        program &&
        program._id &&
        program.edition_ids.length &&
        program.status !== 'draft'
      ),
    [program],
  );

  const isOfferedProgram = useMemo(
    () => !!(program && program.offer_id),
    [program],
  );

  const [isIndividual, setIsIndividual] = useState(false);

  useEffect(() => {
    const lastEdition =
      program && program.editions[program.editions.length - 1];

    if (lastEdition) {
      setIsIndividual(lastEdition.group_size === 1);
    }
  }, [program]);

  useEffect(() => {
    const onProgramTypeChange = (type: ProgramType) => {
      setIsIndividual(type.id === ProgramTypeEnum.INDIVIDUAL.id);
    };

    emitter.on(PROGRAM_TYPE_CHANGE_EVENT, onProgramTypeChange);

    return () => {
      emitter.off(PROGRAM_TYPE_CHANGE_EVENT, onProgramTypeChange);
    };
  }, []);

  const cloneProgram = useCallback(async () => {
    if (!program) {
      return;
    }

    const isConfirmed = await alert.confirm();

    if (!isConfirmed) {
      return;
    }

    logger.event('program_clone_tap');

    navigate(
      routerPath.cloneProgram.$buildPath({
        params: {
          clonedProgramId: program._id,
        },
      }),
      {replace: true},
    );
  }, [navigate, program]);

  const tabs = useMemo(() => {
    const tabProps: TabProps = {
      cloneProgram,
      isActive: false,
      isIndividual,
      isOfferedProgram,
      program,
      shouldCloneProgram,
      userIsOwner,
    };

    return [
      {
        content: (
          <Description
            {...tabProps}
            isActive={activeTabIndex === 0}
            ref={refs.description}
          />
        ),
        key: 'description',
        title: i18n.t('shared:CRUProgram.description_tab'),
      },
      {
        content: (
          <Details
            {...tabProps}
            isActive={activeTabIndex === 1}
            ref={refs.details}
          />
        ),
        key: 'details',
        title: i18n.t('shared:CRUProgram.details_tab'),
      },
      {
        content: (
          <Schedule
            {...tabProps}
            isActive={activeTabIndex === 2}
            ref={refs.schedule}
          />
        ),
        key: 'schedule',
        title:
          /**
           * @i18n_parser
           *
           * t('shared:CRUProgram.tasks_tab')
           * t('shared:CRUProgram.schedule_tab')
           */
          i18n.t(
            `shared:CRUProgram.${isIndividual ? 'tasks_tab' : 'schedule_tab'}`,
          ),
      },
      {
        content: (
          <Library
            {...tabProps}
            isActive={activeTabIndex === 3}
            ref={refs.library}
          />
        ),
        key: 'library',
        title: i18n.t('shared:CRUProgram.library_tab'),
      },
      {
        content: (
          <Goals
            {...tabProps}
            goalsStore={goalsStore}
            isActive={activeTabIndex === 4}
            ref={refs.goals}
          />
        ),
        key: 'goals',
        title: i18n.t('shared:CRUProgram.goals_tab'),
      },
      {
        content: (
          <Complete
            {...tabProps}
            isActive={activeTabIndex === 5}
            ref={refs.complete}
          />
        ),
        key: 'complete',
        title: i18n.t('shared:CRUProgram.complete_tab'),
      },
    ] as Tab[];
  }, [
    program,
    userIsOwner,
    isOfferedProgram,
    shouldCloneProgram,
    cloneProgram,
    isIndividual,
    refs.description,
    refs.details,
    refs.schedule,
    refs.library,
    refs.goals,
    refs.complete,
    activeTabIndex,
    goalsStore,
  ]);

  return {refs, tabs};
};

export default useTabs;
